import { createApp, type App } from 'vue'
import { ConvexClient } from 'convex/browser'
import { convexVue } from 'convex-vue'
import PluginWidgetApp from './PluginWidgetApp.vue'
import { usePluginSession, type PluginSessionConfig } from './composables/usePluginSession'
import { usePostHog } from './composables/usePostHog'
import { createShadowContainer, injectStyles, protectPortalContainer } from './shadowDomSetup'

// Import Tailwind CSS entry point - Vite plugin processes this
// The ?inline suffix imports CSS as a string for shadow DOM injection
import shadowStyles from './styles/tailwind.css?inline'
import customStyles from './styles/custom.css?inline'

// Portal styles for dialogs in light DOM
import { portalStyles } from './styles.portal'

// Import Convex API
import { api } from '~/convex/_generated/api'

// Global state
let app: App | null = null
let hostContainer: HTMLElement | null = null
let shadowRoot: ShadowRoot | null = null
let portalContainer: HTMLElement | null = null
let portalStyleElement: HTMLStyleElement | null = null

function convertRemToPx(css: string): string {
  return css.replace(/(-?\d*\.?\d+)rem\b/g, (_, value: string) => {
    const pixels = Number.parseFloat(value) * 16
    const normalized = Number.isInteger(pixels) ? String(pixels) : String(Number.parseFloat(pixels.toFixed(4)))
    return `${normalized}px`
  })
}

interface AbbaChat {
  init(config: PluginSessionConfig): Promise<void>
  destroy(): void
}

function normalizeUrl(url: string | undefined): string | undefined {
  return url?.trim().replace(/\/+$/, '')
}

/**
 * Initialize the ABBA Chat widget
 */
async function init(config: PluginSessionConfig): Promise<void> {
  try {
    // Validate config
    if (!config.clientId || !config.userId || !config.userName || !config.sessionToken || !config.productId) {
      const error = new Error('Missing required configuration parameters. Please provide clientId, userId, userName, sessionToken, and productId.')
      console.error('[ABBA Chat] Configuration error:', error)
      throw error
    }

    // Use default Convex URL if not provided
    const convexUrl = config.convexUrl || import.meta.env.VITE_CONVEX_URL
    if (!convexUrl) {
      const error = new Error('Convex URL not provided and no default available')
      console.error('[ABBA Chat] Configuration error:', error)
      throw error
    }

    // Create Convex client
    const convex = new ConvexClient(convexUrl)

    // Get session manager
    const session = usePluginSession()

    // Try to reuse existing session from localStorage
    let sessionData
    const existingSession = session.loadSession()

    // Check if existing session matches current config
    const normalizedConvexUrl = normalizeUrl(convexUrl)
    const sessionMatchesConfig = existingSession
      && existingSession.externalUserId === config.userId
      && existingSession.externalUserName === config.userName
      && existingSession.externalClientId === config.clientId
      && existingSession.externalProductId === config.productId
      && normalizeUrl(existingSession.convexUrl) === normalizedConvexUrl

    if (existingSession && sessionMatchesConfig) {
      console.log('[ABBA Chat] Found existing session in localStorage, validating...')

      // Validate the session with the backend
      try {
        const validationResult = await convex.query(api.pluginAuth.validatePluginSession, {
          sessionToken: existingSession.pluginSessionToken
        })

        if (validationResult && validationResult.isValid) {
          console.log('[ABBA Chat] Existing session is valid, reusing it')
          sessionData = existingSession
        } else {
          console.log('[ABBA Chat] Existing session is invalid or expired, creating new session')
          sessionData = null
        }
      } catch (validationError) {
        console.warn('[ABBA Chat] Session validation failed, will create new session:', validationError)
        sessionData = null
      }
    } else if (existingSession && !sessionMatchesConfig) {
      console.log('[ABBA Chat] Existing session does not match config (user changed), creating new session')
      sessionData = null
    }

    // Create new session if we don't have a valid one
    if (!sessionData) {
      console.log('[ABBA Chat] Creating new plugin session...')
      try {
        const newSession = await convex.mutation(api.pluginAuth.createPluginSession, {
          clientId: config.clientId,
          userId: config.userId,
          userName: config.userName,
          sessionToken: config.sessionToken,
          productId: config.productId
        })

        sessionData = {
          ...newSession,
          externalUserId: config.userId,
          externalUserName: config.userName,
          externalClientId: config.clientId,
          externalProductId: config.productId,
          convexUrl: normalizedConvexUrl
        }

        console.log('[ABBA Chat] New session created successfully')
      } catch (sessionError) {
        const error = new Error(`Failed to create session: ${sessionError instanceof Error ? sessionError.message : 'Unknown error'}`)
        console.error('[ABBA Chat] Session creation failed:', error)
        throw error
      }
    }

    // Store session data (either reused or newly created)
    session.setSession(sessionData)

    // Initialize PostHog tracking
    const posthog = usePostHog()
    posthog.init({})

    // Identify user for tracking
    if (posthog.isInitialized.value) {
      posthog.identify(config.userId, {
        name: config.userName,
        clientId: config.clientId,
        productId: config.productId,
        email: config.userId // Assuming userId might be email
      })
    }

    // Create host container for shadow DOM
    hostContainer = document.createElement('div')
    hostContainer.id = 'abba-chat-widget-host'
    document.body.appendChild(hostContainer)

    // Create shadow DOM
    shadowRoot = createShadowContainer(hostContainer)

    // Inject shadow styles into shadow DOM (Tailwind + custom styles)
    injectStyles(shadowRoot, convertRemToPx(shadowStyles + customStyles))

    // Inject portal styles into document head (for dialogs in light DOM)
    portalStyleElement = document.createElement('style')
    portalStyleElement.setAttribute('data-abba-portal-styles', 'true')
    portalStyleElement.textContent = convertRemToPx(portalStyles)
    document.head.appendChild(portalStyleElement)

    // Create portal container for dialogs (outside shadow DOM)
    portalContainer = document.createElement('div')
    portalContainer.id = 'abba-portal-container'
    portalContainer.setAttribute('data-abba-portal', 'true')
    protectPortalContainer(portalContainer)
    document.body.appendChild(portalContainer)

    // Get the shadow mount point
    const shadowMountPoint = shadowRoot.querySelector('#shadow-mount')
    if (!shadowMountPoint) {
      throw new Error('Shadow mount point not found')
    }

    // Create and mount Vue app with convex-vue plugin
    app = createApp(PluginWidgetApp, {
      convexUrl
    })

    // Use convex-vue plugin
    app.use(convexVue, {
      url: convexUrl
    })

    // Mount Vue app inside shadow DOM
    app.mount(shadowMountPoint)

    console.log('[ABBA Chat] Widget initialized successfully with Shadow DOM')
  } catch (error) {
    console.error('[ABBA Chat] Initialization failed:', error)
    const session = usePluginSession()
    session.setError(error instanceof Error ? error.message : 'Unknown error')
    throw error
  }
}

/**
 * Destroy the widget and clean up
 */
function destroy(): void {
  // Unmount Vue app
  if (app) {
    app.unmount()
    app = null
  }

  // Remove host container (contains shadow DOM)
  if (hostContainer && hostContainer.parentNode) {
    hostContainer.parentNode.removeChild(hostContainer)
    hostContainer = null
  }

  // Remove portal container
  if (portalContainer && portalContainer.parentNode) {
    portalContainer.parentNode.removeChild(portalContainer)
    portalContainer = null
  }

  // Remove portal styles
  if (portalStyleElement && portalStyleElement.parentNode) {
    portalStyleElement.parentNode.removeChild(portalStyleElement)
    portalStyleElement = null
  }

  // Clear shadow root reference
  shadowRoot = null

  // Clear session
  const session = usePluginSession()
  session.clearSession()

  console.log('[ABBA Chat] Widget destroyed')
}

// Expose global API
const AbbaChat: AbbaChat = {
  init,
  destroy
}

// Export for module usage
export default AbbaChat

// Expose on window for script tag usage
if (typeof window !== 'undefined') {
  (window as any).AbbaChat = AbbaChat
}
